import NiceModal from "@ebay/nice-modal-react";
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { ACCOUNT_UPDATE_V2 } from "@/api/apiEndpoints";
import { canv } from "@/canv/global";
import { getTarget, MakeRequest } from "@/canv/utils";
import { loadFiles, pasteProccessor } from "@/canvasController/helpers";
import { eventEmitter } from "@/eventEmitter";
import i18n, { normalizeLanguage } from "@/i18n";
import { ShareWindow } from "@/interface/ShareWindow";
import { selectionManager } from "@/tools/cursorTool";
import { SettingsWindowLayout } from "./SettingsWindowLayout";
import styles from "./settingsWindow.module.css";

export class SettingsWindow {
    show() {
        this.wrapper = document.createElement("div");
        this.wrapper.classList.add(styles.settingsWindowWrapper);

        const settingsWindowLayout = React.createElement(SettingsWindowLayout, {
            onClick: this.callProccessorByName.bind(this),
        });

        this.root = createRoot(this.wrapper);
        this.root.render(settingsWindowLayout);

        document.body.appendChild(this.wrapper);

        this.initWrapper();

        eventEmitter.dispatch("header.button.active.name", {
            name: "settings",
        });
    }

    initWrapper() {
        const settingsWindowWrapper = document.getElementsByClassName(
            styles.settingsWindowWrapper,
        )[0];
        settingsWindowWrapper.addEventListener("pointerdown", this.wrapperClick, false);
    }

    wrapperClick = (e) => {
        const tryInner = getTarget(e, styles.settingsWindow);

        if (tryInner == null) {
            this.close();
        }
    };

    close() {
        this.root?.unmount();
        this.wrapper?.parentNode?.removeChild(this.wrapper);

        eventEmitter.dispatch("header.button.active.name", {
            name: null,
        });
    }

    callProccessorByName(name: string) {
        if (name === "placeImage") {
            this.placeImageProccessor();
        } else if (name === "share") {
            NiceModal.show(ShareWindow, { onlyRead: canv.getInitDataProp("onlyRead") });
            this.close();
        } else if (name === "shortcuts") {
            canv.appInterface.shortcutsWindow.show();
            this.close();
        } else if (name === "undo") {
            canv.eventManager.Undo();
        } else if (name === "redo") {
            canv.eventManager.Redo();
        } else if (name === "copy") {
            canv.canvasController.copyProccessor();
        } else if (name === "paste") {
            pasteProccessor();
        } else if (name === "duplicate") {
            canv.canvasController.duplicateProccessor();
        } else if (name === "selectAll") {
            selectionManager.setSelected(canv.canvasController.getFirstLayerGroups());
        } else if (name === "alignLeft") {
            canv.canvasController.alignSelected("left");
        } else if (name === "alignHCenters") {
            canv.canvasController.alignSelected("hCenters");
        } else if (name === "alignRight") {
            canv.canvasController.alignSelected("right");
        } else if (name === "alignTop") {
            canv.canvasController.alignSelected("top");
        } else if (name === "alignVCenters") {
            canv.canvasController.alignSelected("vCenters");
        } else if (name === "alignBottom") {
            canv.canvasController.alignSelected("bottom");
        } else if (name === "zoomToSelection") {
            canv.zoomToSelected();
        } else if (name === "zoomIn") {
            canv.actionManager.zoomPlus();
        } else if (name === "zoomOut") {
            canv.actionManager.zoomMinus();
        } else if (name === "navTrackpad") {
            canv.actionManager.setMode("trackpad");
            this.close();
        } else if (name === "navMouse") {
            canv.actionManager.setMode("mouse");
            this.close();
        } else if (name === "setLanguageRu") {
            this.changeLanguage("ru");
        } else if (name === "setLanguageEn") {
            this.changeLanguage("en");
        } else if (name === "backToFiles") {
            window.location.href = `${canv.host}/dashboard`;
        }
    }

    async changeLanguage(language) {
        const currentLanguage = normalizeLanguage(i18n.language);
        if (currentLanguage === language) {
            this.close();
            return;
        }

        if (canv.isGuest()) {
            window.localStorage.setItem("interfaceLanguage", language);
            window.location.reload();
            return;
        }

        try {
            await MakeRequest(ACCOUNT_UPDATE_V2, {
                token: canv.getInitDataProp("userToken"),
                interfaceLanguage: language,
            });

            window.localStorage.setItem("interfaceLanguage", language);
            window.location.reload();
        } catch {
            canv.captureException(
                new Error("Не удалось сохранить язык интерфейса: проблема с сетью"),
            );
        }
    }

    placeImageProccessor() {
        const imageWidgetInput = document.getElementsByClassName(
            styles.settingsWindowItemImage,
        )[0] as HTMLInputElement;
        imageWidgetInput.click();

        imageWidgetInput.removeEventListener("change", this.placeImageProccessorHelper, false);
        imageWidgetInput.addEventListener("change", this.placeImageProccessorHelper, false);
    }

    placeImageProccessorHelper = (e) => {
        if (e.target.files.length === 0) {
            return;
        }
        return loadFiles(e.target.files, "center")?.then((newGroup) => {
            if (Array.isArray(newGroup) && newGroup.length > 0) {
                selectionManager.setSelected(newGroup);
            } else {
                canv.toolBox.switchTo(canv.toolBox.getFirstTool());
            }
        });
    };

    root: Root | null = null;
    wrapper: HTMLElement | null = null;
}
